import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchDashboardLayout, resetDashboardLayout, saveDashboardLayout } from "../../../api/apiClient";
import {
  appendEntry, layoutFromSaved, layoutToSaved, moveEntry, removeEntry, reorderEntry, resizeEntry,
} from "./widgetLayout";

const SAVE_DEBOUNCE_MS = 500;

/**
 * State for a customizable dashboard: the widget layout (loaded from / saved to the server per user,
 * tenant and role), edit mode, the widget library drawer and drag-to-reorder.
 * `registry` and `defaults` must be stable references (module constants).
 */
export default function useWidgetLayout({ role, tenantId, registry, defaults }) {
  const [layout, setLayout] = useState(defaults);
  const [loading, setLoading] = useState(Boolean(tenantId));
  const [saveError, setSaveError] = useState(null);
  const [edit, setEditState] = useState(false);
  const [lib, setLib] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const timer = useRef(null);
  const pending = useRef(null);

  const persist = useCallback((next) => {
    if (!tenantId) return;
    pending.current = next;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveDashboardLayout(role, layoutToSaved(pending.current, registry), tenantId)
        .then(() => setSaveError(null))
        .catch(() => setSaveError("Unable to save dashboard layout"));
    }, SAVE_DEBOUNCE_MS);
  }, [role, tenantId, registry]);

  useEffect(() => {
    let cancelled = false;
    setLayout(defaults);
    setSaveError(null);
    if (!tenantId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchDashboardLayout(role, tenantId)
      .then((saved) => { if (!cancelled) setLayout(layoutFromSaved(saved, registry, defaults)); })
      .catch(() => { if (!cancelled) setSaveError("Unable to load saved dashboard layout"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      // A tenant change or unmount used to drop a save that was still inside the debounce window,
      // so a remove clicked just after load never reached the server.
      const queued = pending.current;
      clearTimeout(timer.current);
      pending.current = null;
      if (queued && tenantId) {
        saveDashboardLayout(role, layoutToSaved(queued, registry), tenantId).catch(() => {});
      }
    };
  }, [role, tenantId, registry, defaults]);

  const update = useCallback((fn) => {
    setLayout((current) => {
      const next = fn(current);
      if (next !== current) persist(next);
      return next;
    });
  }, [persist]);

  const endDrag = useCallback(() => { setDragging(null); setOver(null); }, []);

  return useMemo(() => ({
    registry, layout, loading, saveError, edit, lib, dragging, over,
    setLib,
    setEdit: (value) => { setEditState(value); if (!value) setLib(false); },
    has: (id) => layout.some(([entryId]) => entryId === id),
    add: (id) => update((current) => appendEntry(current, id, registry)),
    remove: (id) => update((current) => removeEntry(current, id)),
    resize: (id, size) => update((current) => resizeEntry(current, id, size)),
    move: (index, delta) => update((current) => moveEntry(current, index, delta)),
    startDrag: setDragging,
    enterDrag: (id) => setOver(id !== dragging ? id : null),
    endDrag,
    drop: (id) => { if (dragging && dragging !== id) update((current) => reorderEntry(current, dragging, id)); endDrag(); },
    reset: async () => {
      try {
        if (tenantId) await resetDashboardLayout(role, tenantId);
        clearTimeout(timer.current);
        pending.current = null;
        setSaveError(null);
        setLayout(defaults);
      } catch (error) {
        setSaveError("Unable to reset dashboard layout");
      }
    },
    retrySave: () => { if (pending.current) persist(pending.current); },
  }), [registry, defaults, layout, loading, saveError, edit, lib, dragging, over, update, endDrag, persist, role, tenantId]);
}
